import { useParams } from "react-router-dom";
import { Navbar } from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import { motion } from "framer-motion";
import { Clock, Calendar, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ARTICLES_DATA } from "../data/articlesData";

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Find article by slug
  const article = ARTICLES_DATA.find(a => a.slug === slug);

  // If article not found, show 404-like message
  if (!article) {
    return (
      <div>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-black mb-4">Article Not Found</h1>
            <button
              onClick={() => navigate("/")}
              className="text-red-600 font-bold hover:underline"
            >
              ← Back to Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const getCategoryColor = (category) => {
    const colors = {
      "Business Strategy": "#dc2626",
      "Cost Optimization": "#16a34a",
      "Compliance": "#3b82f6",
      "IT Services": "#7c3aed",
      "Manpower": "#ea580c"
    };
    return colors[category] || "#64748b";
  };

  const categoryColor = getCategoryColor(article.category);

  return (
    <div>
      <Navbar />
      
      {/* Article Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-red-600 font-semibold mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Home
          </motion.button>

          {/* Category Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <span
              className="inline-block px-4 py-2 rounded-full text-sm font-bold text-white mb-6"
              style={{ backgroundColor: categoryColor }}
            >
              {article.category}
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-6 font-heading"
          >
            {article.title}
          </motion.h1>

          {/* Meta Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex items-center gap-6 text-slate-600 mb-8"
          >
            <div className="flex items-center gap-2">
              <Calendar size={18} />
              <span className="text-sm font-semibold">
                {new Date(article.publishedDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={18} />
              <span className="text-sm font-semibold">{article.readTime}</span>
            </div>
          </motion.div>

          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-2xl overflow-hidden shadow-2xl mb-12"
          >
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-[400px] object-cover"
            />
          </motion.div>

          {/* Article Content Placeholder */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="prose prose-lg max-w-none"
          >
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              {article.description}
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-8 rounded-r-lg">
              <p className="text-slate-700 font-semibold">
                📝 <strong>Note:</strong> This is a placeholder article page. Full content will be added soon!
              </p>
              <p className="text-slate-600 text-sm mt-2">
                In production, this page would contain the full article content, including sections, images, code snippets, and more.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-4 mt-12">Coming Soon</h2>
            <p className="text-slate-600 leading-relaxed">
              We're working on creating comprehensive, valuable content for this article. 
              Check back soon or subscribe to our newsletter to get notified when new articles are published.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
